/**
 * COMPAT-3: the trust settings a person must be able to act on from the console alone.
 *
 *   RemoteMachines   the owner's switch for agents on other computers (new hubs start with it
 *                    off), with the warning while it is on
 *   NodeAttention    machines that wait for a person: a legacy machine's proved key to approve
 *                    or dismiss (any recently proved fingerprint may be approved), and an
 *                    identity seen in two places (quarantined) to release
 *
 * Errors show inline in the panel, never as a toast.
 */
import { useState } from 'react';
import { REMOTE_MACHINES_WARNING } from '@orgus/protocol';
import type { NodeTrustInfo, TrustState } from '@orgus/protocol';
import { Badge, Button, ConfirmDialog, ErrorState, Panel, RelativeTime, Select, Switch } from '@orgus/ui';
import { KeyRound, MonitorSmartphone, ShieldAlert, ShieldOff, TriangleAlert } from '@orgus/ui/icons';
import { useCan } from '../auth/useMe.ts';
import { useOrgus } from '../../lib/store.ts';
import type { TrustApi } from './useTrust.ts';

export function RemoteMachines({ t, s }: { t: TrustApi; s: TrustState }) {
  const isOwner = useCan('owner');
  const [confirmOn, setConfirmOn] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const state = s.remoteMachines;
  if (!state) return null;
  const set = async (on: boolean) => {
    setError(null);
    try {
      await t.saveSettings({ remoteMachines: on });
    } catch (e) {
      setError(e);
    }
  };
  return (
    <Panel title="Machines on other computers" icon={<MonitorSmartphone />} sub={state.allowed ? 'Allowed' : 'Only this computer'}>
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <label htmlFor="trust-remote-machines" className="text-sm font-medium text-fg">
              Let agents on other computers connect
            </label>
            <p id="trust-remote-machines-hint" className="text-sm text-fg-muted">
              {state.allowed
                ? 'Agents on other computers can connect. Turn this off to accept only this computer again; machines already added keep their place and reconnect when it is on.'
                : 'Only agents on this computer can connect. A second computer needs this on before its join command works.'}
              {!isOwner && ' Only the owner can change it.'}
            </p>
          </div>
          <Switch
            id="trust-remote-machines"
            aria-describedby="trust-remote-machines-hint"
            checked={state.allowed}
            disabled={!isOwner}
            onCheckedChange={(on) => (on ? setConfirmOn(true) : void set(false))}
          />
        </div>
        {state.allowed && (state.warning || state.legacy) && (
          <div role="alert" className="flex gap-3 rounded-card border border-waiting/30 bg-waiting-soft px-3 py-2">
            <TriangleAlert className="mt-1 size-4 shrink-0 text-waiting" aria-hidden />
            <div className="min-w-0 text-sm text-fg-muted">
              {state.warning ?? REMOTE_MACHINES_WARNING}
              {state.legacy && ' It is on because this hub accepted other computers before this setting existed.'}
            </div>
          </div>
        )}
        {error != null && <ErrorState variant="inline" error={error} />}
      </div>
      <ConfirmDialog
        open={confirmOn}
        onOpenChange={setConfirmOn}
        title="Let machines on other computers connect?"
        description={REMOTE_MACHINES_WARNING}
        confirmLabel="Allow other computers"
        onConfirm={() => set(true)}
      />
    </Panel>
  );
}

type Pending = { kind: 'approve'; n: NodeTrustInfo } | { kind: 'dismiss'; n: NodeTrustInfo } | { kind: 'release'; n: NodeTrustInfo };

export function NodeAttention({ t, s }: { t: TrustApi; s: TrustState }) {
  const machines = useOrgus((st) => st.machines);
  const isAdmin = useCan('admin');
  const [pending, setPending] = useState<Pending | null>(null);
  const [pick, setPick] = useState('');
  const [error, setError] = useState<unknown>(null);
  const rows = s.nodes.filter((n) => n.pendingKey || n.quarantine);
  if (!rows.length) return null;
  const nameOf = (n: NodeTrustInfo) => machines[n.machineId]?.name ?? n.machineId;
  const run = async (f: () => Promise<void>) => {
    setError(null);
    try {
      await f();
    } catch (e) {
      setError(e);
      throw e;
    }
  };
  const keys = (n: NodeTrustInfo) => (n.pendingKey ? [n.pendingKey.fingerprint, ...(n.pendingKey.others ?? []).map((o) => o.fingerprint)] : []);

  return (
    <Panel title="Machines that need you" icon={<ShieldAlert />} sub={`${rows.length} waiting`} flush>
      <ul className="divide-y divide-border-subtle">
        {rows.map((n) => (
          <li key={n.machineId} className="flex items-start gap-3 px-4 py-3">
            {n.quarantine ? <ShieldOff className="mt-1 size-4 shrink-0 text-failed" aria-hidden /> : <KeyRound className="mt-1 size-4 shrink-0 text-waiting" aria-hidden />}
            <div className="min-w-0 flex-1 text-sm">
              <div className="flex items-center gap-2 text-fg">
                <span className="truncate font-medium">{nameOf(n)}</span>
                <span className={n.quarantine ? 'text-failed' : 'text-waiting'}>{n.quarantine ? 'Quarantined' : 'Key waiting for approval'}</span>
              </div>
              {n.quarantine ? (
                <div className="text-fg-muted">
                  Its identity was seen in two places at once ({n.quarantine.remotes.join(' and ') || 'unknown addresses'}), <RelativeTime ts={n.quarantine.at} />. Write actions to it are refused. Release it once you know which computer is the real one.
                </div>
              ) : (
                n.pendingKey && (
                  <div className="flex flex-col gap-1 text-fg-muted">
                    <span>
                      Key <code className="break-all font-mono text-caption text-fg">{n.pendingKey.fingerprint}</code> from {n.pendingKey.remote || 'an unknown address'}, last seen <RelativeTime ts={n.pendingKey.lastSeenAt} />. Approve it only if <code className="font-mono text-caption">orgus agent key</code> on {nameOf(n)} shows the same fingerprint.
                    </span>
                    {(n.pendingKey.others?.length ?? 0) > 0 && (
                      <span className="flex items-center gap-2">
                        <Badge tone={n.pendingKey.frozen ? 'failed' : 'waiting'} variant="outline">
                          {n.pendingKey.frozen ? 'Key keeps changing' : `${n.pendingKey.others!.length} other key${n.pendingKey.others!.length === 1 ? '' : 's'}`}
                        </Badge>
                        <span>Two copies of this machine may be running: compare the fingerprint before you approve.</span>
                      </span>
                    )}
                  </div>
                )
              )}
            </div>
            {isAdmin && (
              <div className="flex shrink-0 gap-2">
                {n.quarantine ? (
                  <Button size="sm" variant="outline" onClick={() => setPending({ kind: 'release', n })}>
                    Release machine
                  </Button>
                ) : (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => setPending({ kind: 'dismiss', n })}>
                      Dismiss key
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setPick(n.pendingKey?.fingerprint ?? '');
                        setPending({ kind: 'approve', n });
                      }}
                    >
                      Approve key
                    </Button>
                  </>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
      {error != null && (
        <div className="border-t border-border-subtle px-4 py-3">
          <ErrorState variant="inline" error={error} />
        </div>
      )}
      {!isAdmin && <p className="border-t border-border-subtle px-4 py-3 text-caption text-fg-subtle">An admin approves keys and releases machines.</p>}

      <ConfirmDialog
        open={pending?.kind === 'approve'}
        onOpenChange={(o) => !o && setPending(null)}
        title={pending ? `Approve the key of ${nameOf(pending.n)}?` : ''}
        description="From now on this machine must prove this key on every connection, and a copy with another key is refused."
        confirmLabel="Approve key"
        tone="primary"
        onConfirm={() => (pending ? run(() => t.approveKey(pending.n.machineId, pick)) : Promise.resolve())}
      >
        {pending && keys(pending.n).length > 1 ? (
          <Select label="The fingerprint the machine shows" options={keys(pending.n).map((k) => ({ value: k, label: k }))} value={pick} onValueChange={setPick} />
        ) : (
          <code className="break-all font-mono text-caption text-fg">{pick}</code>
        )}
      </ConfirmDialog>
      <ConfirmDialog
        open={pending?.kind === 'dismiss'}
        onOpenChange={(o) => !o && setPending(null)}
        title={pending ? `Dismiss the waiting key of ${nameOf(pending.n)}?` : ''}
        description="The key is forgotten. If the machine proves a key on its next connection, it waits here again."
        confirmLabel="Dismiss key"
        onConfirm={() => (pending ? run(() => t.dismissKey(pending.n.machineId)) : Promise.resolve())}
      />
      <ConfirmDialog
        open={pending?.kind === 'release'}
        onOpenChange={(o) => !o && setPending(null)}
        title={pending ? `Release ${nameOf(pending.n)}?` : ''}
        description="Write actions to it are allowed again. Do this only once you know the other copy of its identity is gone; otherwise remove the machine in Machines and enrol it again."
        confirmLabel="Release machine"
        onConfirm={() => (pending ? run(() => t.release(pending.n.machineId)) : Promise.resolve())}
      />
    </Panel>
  );
}
